import * as React from "react";
import { createRoot } from "react-dom/client";
import { BaseBlock, broadcastBlockLoad, ErrorBoundary } from "@draftkings/sb-resp-block-lib";
import { getConfig } from "./config";
import { dict } from "./dict";
import { MyBetsList } from "./ui";
import { MyBetsListState } from "./state";
import { Parser } from "../../Parser";
import { Retriever, BetsRetriever, CashOutRetriever, BonusesRetriever } from "../../Retriever";
import { myBetsTheme } from "./themeOverrides/myBetsTheme";
import { CashOutConnection, BonusDetailsConnection, MyBetsSocketConnection } from "../../DataLayer";
import { Tracker } from "../../helpers";
import { setTestIdAttributes, setTestIdAttributesInProd } from "@draftkings/arena-web";
import { getTruthyValue } from "@draftkings/sportsbook-common-utils";
export class MyBetsListRoot extends BaseBlock {
    constructor(domId, options) {
        super(domId, options);
        this.activate = () => {
            super.beforeActivate();
            const domRef = document.getElementById(this.domId);
            if (!domRef) {
                console.warn(`DOM element with id "${this.domId}" couldn't be located on the page.`);
            }
            if (domRef) {
                this.root = createRoot(domRef);
                this.root.render(React.createElement(ErrorBoundary, null, this.getComponent()));
                this.isActive = true;
            }
            super.afterActivate();
            return this;
        };
        this.renewToken = (token) => {
            var _a, _b;
            if (this.isActive) {
                this.options.token = token;
                (_b = (_a = this.myBetsConnection).setAuthToken) === null || _b === void 0 ? void 0 : _b.call(_a, token);
            }
        };
        setTestIdAttributes(["data-test-id"]);
        setTestIdAttributesInProd(["data-test-id"]);
        const dictionary = (options.dict || { myBetsDictionary: dict });
        const config = getConfig(dictionary);
        const mergedConfig = Object.assign(Object.assign({}, config), (options.config || {}));
        const connection = new MyBetsSocketConnection({
            url: options.shelbyPushWSUrl,
            token: options.token,
            getAsyncSocket: options.getAsyncSocket,
            onTokenNotValid: options.requestToken,
            additionalDelay: 1000,
            isTelemetryEnabled: this.getOption(options, "isTelemetryEnabled", false),
            isMsgPack: this.getOption(options, "isMsgPack", false),
            locale: options.locale || "en",
        });
        this.myBetsConnection = connection;
        const betsRetriever = new BetsRetriever({
            config: {
                numberOfBets: mergedConfig.loadBetsCount,
                status: mergedConfig.defaultStatusFilter,
                maxBetsSizeReconnect: mergedConfig.maxBetsSizeReconnect
            },
            isLoggedInOnLoad: options.isLoggedInOnLoad,
            updateHeaderBalance: options.updateHeaderBalance,
            isMsgpack: this.getOption(options, "isMsgPack", false)
        });
        const bonusesRetriever = new BonusesRetriever({
            connection: new BonusDetailsConnection({
                client: options.bonusDetailsClient,
                getSiteExperience: options.getSiteExperience,
            }),
        });
        const retriever = new Retriever({
            betsRetriever,
            connection,
            cashOutRetriever: new CashOutRetriever({
                connection: new CashOutConnection({
                    client: options.cashOutClient,
                    siteExperience: options.getSiteExperience(),
                }),
                onBetCashedOut: betsRetriever.onBetCashedOut,
                isLoggedInOnLoad: options.isLoggedInOnLoad,
                isMsgpack: this.getOption(options, "isMsgPack", false),
            }),
            bonusesRetriever,
            isLoggedInOnLoad: options.isLoggedInOnLoad,
        });
        const parser = new Parser({
            retriever,
            teamLogos: this.getOption(options, "logos", {}),
            dictionary,
            currencyCodeMap: options.currencyCodeMap,
            currencyFormatter: options.currencyFormatter,
            dateFormatter: options.dateFormatter,
            formatDateToFullMonthDayYear: this.getOption(options, "formatDateToFullMonthDayYear", options.dateFormatter),
            // TODO: Temporary solution to pass the locale to the modal in the consumer. In the real implementation the SB/Consumer should know the locale
            openModal: (args) => options.openBoostModal(Object.assign(Object.assign({}, args), { locale: options.locale || "en" })),
            openPromosPage: options.openPromosPage,
            isLoggedInCanBet: options.isLoggedInCanBet,
            openRecheckLocationModal: options.openRecheckLocationModal,
            staticS3Host: options.staticS3Host,
            swooshFeatureFlag: !!options.swooshFeatureFlag,
            swooshBaseUrl: getTruthyValue(options.swooshBaseUrl, ""),
            openBetOverMaxAmountModal: (data) => { var _a; return (_a = options.openBetOverMaxAmountModal) === null || _a === void 0 ? void 0 : _a.call(options, data); },
            locale: options.locale || "en",
        });
        this.dataProvider = new MyBetsListState({
            config: mergedConfig,
            dictionary,
            parser,
            openEarlyExitModal: () => {
                var _a;
                return (_a = options.openEarlyExitModal) === null || _a === void 0 ? void 0 : _a.call(options, {
                    heading: dictionary.myBetsDictionary.EarlyExit,
                    description: dictionary.myBetsDictionary.EarlyExitDescription,
                    buttonText: dictionary.myBetsDictionary.BoostDetailsButtonText,
                    secondaryButtonText: dictionary.myBetsDictionary.ViewHelpCenter,
                    locale: options.locale || "en",
                });
            },
            getNavLink: this.getOption(options, "getNavLink", (props) => (React.createElement(React.Fragment, null, props.children))),
            getIcon: this.getOption(options, "getIcon", () => React.createElement(React.Fragment, null)),
        });
        Tracker.configure(options.trackEvent);
        this.props = { dataProvider: this.dataProvider, theme: myBetsTheme };
    }
    getComponent() {
        return React.createElement(MyBetsList, Object.assign({}, this.props));
    }
    deactivate() {
        var _a;
        super.beforeDeactivate();
        const domRef = document.getElementById(this.domId);
        if (domRef) {
            (_a = this.root) === null || _a === void 0 ? void 0 : _a.unmount();
            this.isActive = false;
        }
        super.afterDeactivate();
        return this;
    }
    getOption(options, option, defaultValue) {
        return options[option] || defaultValue;
    }
}
window["MyBetsList"] = MyBetsListRoot;
broadcastBlockLoad("MyBetsList");
//# sourceMappingURL=index.js.map